import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useNoticeBannersStackContext, useResponsive } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError, useA11ySelector } from '@order/utils';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
} from '../../components';
import {
  useCustomerProfileFieldsQuery,
  useUpdateCustomerProfileMutation,
} from './GraphQL/Profile.generated';
import { ProfileForm } from './ProfileForm';

// ────────────────────────────────────────────────────────────────────────────────

export const ProfileScreen = () => {
  const { goBack } = useNavigation();
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const a11ySelector = useA11ySelector('account.profile.container');

  // ─── Network Requests ────────────────────────────────────────────────

  const [customerResponse] = useCustomerProfileFieldsQuery({
    requestPolicy: 'cache-and-network',
  });
  const { submitResponse, handleFormSubmit } = useFormSubmit();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoading =
    customerResponse.fetching || !customerResponse.data?.currentCustomer?.id;

  // NOTE: To improve UX, we need to close this screen at the smallest breakpoint (mobile device) on form reset.
  const shouldCloseTheScreenOnFormReset = currentBreakpoint.isXS;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onFormReset = useCallback(() => {
    if (shouldCloseTheScreenOnFormReset) goBack();
  }, [goBack, shouldCloseTheScreenOnFormReset]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer
      screen="profile"
      testID="profile-screen"
      {...a11ySelector.props}
    >
      <AccountScreenTitle title={t('account.profile.title')} />

      <AccountScreenSection style={styles.content}>
        <ProfileForm
          customer={customerResponse.data?.currentCustomer ?? null}
          isLoading={isLoading}
          isSubmitting={submitResponse.fetching}
          submitForm={handleFormSubmit}
          onFormReset={onFormReset}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

type UpdateCallback = ReturnType<typeof useUpdateCustomerProfileMutation>[1];

// ────────────────────────────────────────────────────────────────────────────────

const useFormSubmit = () => {
  const { t } = useLocalizationContext();
  const [submitResponse, updateMutation] = useUpdateCustomerProfileMutation();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  const handleFormSubmit = React.useCallback(
    async (input: Parameters<UpdateCallback>[0]) => {
      const response = await updateMutation(input);
      const systemError = getUrqlError(response.error);

      if (systemError) {
        const userError = t('account.profile.error');

        track('account.profile_save_failed', { userError, systemError });

        addNoticeBanner({
          text: userError,
          palette: 'caution',
        });
      }

      return response;
    },
    [updateMutation, t, track, addNoticeBanner],
  );

  return { submitResponse, handleFormSubmit };
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flexGrow: 0,
  },
});
